import { operationWords, targetWords } from '@/oversight/auditWords';
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { stateWords } from './words';
import { useMintToken } from '@/api/hooks';
import { failureOf, remedy } from '@/api/errors';
import { PageHeader, useFrameMaybe } from '@/app/frame';

import { FilterChip, SearchField } from './fields';
import { useTechnical } from '@/app/technical';
import { Avatar, Badge, Button, EmptyState, Kicker, type Tone } from './primitives';
import { Plain } from '@/app/technical';
import { ContextPanel, DetailsButton, Explainer, type ItemContext } from './context';

/**
 * The record parts of the gallery contract (docs/frontend/handoff, Component
 * Gallery §Table/RowCard, RecordHeader + FactList, Timeline, and the §17
 * token panel the prototype carries). Typed and generic: a list is columns
 * over rows, a record is facts over a thing, and the same parts render a
 * contact, a company and a Deal.
 */

export interface Column<T> {
  label: string;
  get: (row: T) => string | number | null;
  chip?: ((row: T) => { label: string; tone: Tone } | null) | undefined;
  num?: boolean | undefined;
  primary?: boolean | undefined;
  /** A person or an agent in this cell: initials in a ring, and the moss square for an agent. */
  avatar?: ((row: T) => { name: string; agent?: string | undefined } | null) | undefined;
}

export interface Facet<T> {
  label: string;
  get: (row: T) => string | null;
}

/** The prototype's word→tone map: what a status looks like, everywhere. */
export function statusTone(status: string | null | undefined): Tone {
  const s = (status ?? '').toLowerCase();
  if (['won', 'paid', 'delivered', 'active', 'live', 'promoted', 'confirmed', 'client', 'sent_unchanged', 'sent_edited', 'feedback_received'].includes(s)) return 'accent';
  if (['pending', 'open', 'sent', 'requested', 'waiting', 'proposed', 'draft', 'qualified', 'prepared', 'unapproved'].includes(s)) return 'warning';
  if (['lost', 'overdue', 'suspended', 'declined', 'discarded', 'blocked', 'cancelled'].includes(s)) return 'danger';
  return 'neutral';
}

export function statusChip(status: string | null | undefined): { label: string; tone: Tone } | null {
  if (!status) return null;
  return { label: stateWords(status).toUpperCase(), tone: statusTone(status) };
}

export function Table<T extends { id: string }>({
  title,
  blurb,
  rows,
  columns,
  facets = [],
  onOpen,
  openId = null,
  expanded,
  explain,
  emptyTitle = 'Nothing here yet',
  emptyBody,
  testId,
  ask = true,
  onSearch,
}: {
  title: string;
  blurb?: string | undefined;
  rows: T[];
  columns: Column<T>[];
  facets?: Facet<T>[] | undefined;
  onOpen?: ((row: T) => void) | undefined;
  /** The row whose record is open, shown by `expanded` directly beneath it rather than after the whole list. */
  openId?: string | null | undefined;
  expanded?: ((row: T) => ReactNode) | undefined;
  /**
   * What each row says about itself (ADR-0155): a disclosure on the row
   * opens what it is, what it needs, who brought it, who knows more and the
   * best next step, without leaving the list. `null` for a row with nothing
   * more to say; the row then has no disclosure. Required, so a list is
   * never drawn without a decision: `false` is the decision that each row
   * already is its own explanation (a receipt, a log line), and a test
   * wants a line saying why beside it.
   */
  explain: ((row: T) => ItemContext | null) | false;
  emptyTitle?: string | undefined;
  emptyBody?: string | undefined;
  testId?: string | undefined;
  /** "Ask <twin> about this" beside the search (P:337). On unless a list has no subject to ask about. */
  ask?: boolean | undefined;
  /**
   * A list the server pages: the search is also sent there, a moment after
   * typing stops, so a record past the first page is found too.
   */
  onSearch?: ((q: string) => void) | undefined;
}) {
  const frame = useFrameMaybe();
  const [q, setQ] = useState('');
  const [facet, setFacet] = useState<Record<string, string | null>>({});
  const [sort, setSort] = useState<{ column: number; desc: boolean } | null>(null);
  const step = frame?.pageSize ?? 25;
  const [shown, setShown] = useState(step);
  useEffect(() => setShown(step), [step]);
  // The rows whose details are open: several at once, so two can be compared.
  const [detailed, setDetailed] = useState<ReadonlySet<string>>(new Set());
  const toggleDetails = (id: string) =>
    setDetailed((open) => {
      const next = new Set(open);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  useEffect(() => {
    if (!onSearch) return;
    const t = setTimeout(() => onSearch(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q, onSearch]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = rows.filter((row) =>
      needle === '' ||
      columns.some((c) => String(c.get(row) ?? '').toLowerCase().includes(needle)),
    );
    for (const f of facets) {
      const chosen = facet[f.label];
      if (chosen) out = out.filter((row) => f.get(row) === chosen);
    }
    if (sort) {
      const col = columns[sort.column]!;
      out = [...out].sort((a, b) => {
        const av = col.get(a);
        const bv = col.get(b);
        const cmp =
          typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });
        return sort.desc ? -cmp : cmp;
      });
    }
    return out;
  }, [rows, columns, facets, facet, q, sort]);

  return (
    <section className="flex flex-col gap-4" data-testid={testId}>
      {blurb && <p className="m-0 max-w-[72ch] text-body text-secondary"><Plain>{blurb}</Plain></p>}
      <div className="flex flex-wrap items-center gap-3">
        <SearchField label={`Search ${title}`} value={q} onChange={setQ} />
        <span className="text-dense text-meta">
          {filtered.length === rows.length ? `${rows.length} records` : `${filtered.length} of ${rows.length}`}
        </span>
        {ask && frame && (
          <Button variant="quiet" className="ml-auto" onClick={() => frame.ask(`About the ${title}: `)}>
            Ask {frame.twin ?? 'your twin'} about this
          </Button>
        )}
      </div>
      {facets.map((f) => {
        const values = [...new Set(rows.map((r) => f.get(r)).filter((v): v is string => v !== null))].sort();
        if (values.length < 2) return null;
        return (
          <div key={f.label} className="flex flex-wrap items-center gap-2" role="group" aria-label={f.label}>
            <span className="min-w-[78px] text-micro uppercase tracking-label text-meta">{f.label}</span>
            {[null, ...values].map((v) => {
              const on = (facet[f.label] ?? null) === v;
              return (
                <FilterChip key={v ?? 'all'} on={on} onClick={() => setFacet((all) => ({ ...all, [f.label]: v }))}>
                  {v ?? 'All'}
                </FilterChip>
              );
            })}
          </div>
        );
      })}
      {filtered.length === 0 ? (
        <EmptyState
          title={q ? `Nothing matches "${q}"` : emptyTitle}
          body={
            emptyBody ??
            (q
              ? `Search looks in every column. Or ask ${frame?.twin ?? 'your twin'} instead — it can look across everything at once.`
              : 'When there is something, it is listed here.')
          }
          action={
            q
              ? frame
                ? { label: `Ask ${frame.twin ?? 'your twin'} instead`, onClick: () => frame.ask(`Find ${q} in the ${title}.`) }
                : { label: 'Clear search', onClick: () => setQ('') }
              : undefined
          }
        />
      ) : frame?.layout.phone ? (
        <RowCards rows={filtered.slice(0, shown)} columns={columns} onOpen={onOpen} openId={openId} expanded={expanded} explain={explain || undefined} detailed={detailed} onDetails={toggleDetails} more={filtered.length > shown ? () => setShown((n) => n + step) : undefined} total={filtered.length} shown={shown} />
      ) : (
        <div className="overflow-x-auto border border-line">
          <table className="w-full border-collapse" style={{ minWidth: Math.max(520, columns.length * 110) }}>
            <thead className="bg-raised">
              <tr>
                {columns.map((c, i) => (
                  <th key={c.label} className={`border-b border-line px-4 text-left font-medium ${c.num ? 'text-right' : ''}`}>
                    <button
                      type="button"
                      className={`min-h-tap min-w-tap text-micro font-medium uppercase tracking-label ${sort?.column === i ? 'text-ink' : 'text-meta hover:text-ink'}`}
                      onClick={() =>
                        setSort((s) => (s?.column === i ? { column: i, desc: !s.desc } : { column: i, desc: false }))
                      }
                    >
                      {c.label}
                      {sort?.column === i ? (sort.desc ? ' ↓' : ' ↑') : ''}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, shown).map((row) => {
                const isOpen = expanded !== undefined && row.id === openId;
                const context = (explain ? explain(row) : null) ?? null;
                const detailsOpen = context !== null && detailed.has(row.id);
                return (
                <Fragment key={row.id}>
                <tr
                  className={`border-b border-line last:border-b-0 ${onOpen ? 'cursor-pointer hover:bg-raised' : ''} ${isOpen ? 'bg-raised' : ''}`}
                  onClick={onOpen ? () => onOpen(row) : undefined}
                  tabIndex={onOpen ? 0 : undefined}
                  onKeyDown={onOpen ? (e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(row); } } : undefined}
                  aria-expanded={expanded !== undefined ? isOpen : undefined}
                  data-testid="row"
                >
                  {columns.map((c, at) => {
                    const chip = c.chip?.(row);
                    const value = c.get(row);
                    const person = c.avatar?.(row) ?? null;
                    const text = value === null ? '—' : String(value);
                    const cell = chip ? (
                      <Badge tone={chip.tone}>{chip.label}</Badge>
                    ) : person ? (
                      <span className="flex items-center gap-2.5">
                        <Avatar name={person.name} size={28} agent={person.agent ? { label: person.agent } : undefined} />
                        <span>{text}</span>
                      </span>
                    ) : (
                      text
                    );
                    return (
                      <td
                        key={c.label}
                        className={`h-[var(--row-comfortable)] px-4 py-3.5 text-dense ${
                          c.primary ? 'font-semibold text-ink' : 'text-secondary'
                        } ${c.num ? 'text-right tabular-nums' : ''}`}
                      >
                        {at === 0 && context ? (
                          <span className="flex items-center gap-1">
                            <DetailsButton open={detailsOpen} onToggle={() => toggleDetails(row.id)} controls={`details-${row.id}`} label={context.subject.title} />
                            {/* Its own element, so the cell's words stay one thing to read and to find. */}
                            <span>{cell}</span>
                          </span>
                        ) : (
                          cell
                        )}
                      </td>
                    );
                  })}
                </tr>
                {detailsOpen && (
                  <tr className="border-b border-line last:border-b-0">
                    <td colSpan={columns.length} className="p-4">
                      <ContextPanel context={context} id={`details-${row.id}`} />
                    </td>
                  </tr>
                )}
                {isOpen && (
                  <tr className="border-b border-line last:border-b-0">
                    <td colSpan={columns.length} className="p-4">{expanded(row)}</td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
          {filtered.length > shown && (
            <div className="border-t border-line p-3">
              <Button variant="quiet" onClick={() => setShown((n) => n + step)}>
                Show more · {shown} of {filtered.length}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * The gallery's phone card (RowCard): on a phone a table is a stack, one
 * card a row — its first, bold column as the title and every other column
 * as a quiet label and its value — so nothing scrolls sideways.
 */
function RowCards<T extends { id: string }>({
  rows,
  columns,
  onOpen,
  openId,
  expanded,
  explain,
  detailed,
  onDetails,
  more,
  total,
  shown,
}: {
  rows: T[];
  columns: Column<T>[];
  onOpen?: ((row: T) => void) | undefined;
  openId?: string | null | undefined;
  expanded?: ((row: T) => ReactNode) | undefined;
  explain?: ((row: T) => ItemContext | null) | undefined;
  detailed: ReadonlySet<string>;
  onDetails: (id: string) => void;
  more?: (() => void) | undefined;
  total: number;
  shown: number;
}) {
  const lead = columns.find((c) => c.primary) ?? columns[0]!;
  const rest = columns.filter((c) => c !== lead);
  return (
    <div className="flex flex-col gap-px border border-line bg-line">
      {rows.map((row) => {
        const person = lead.avatar?.(row) ?? null;
        const title = lead.get(row);
        const isOpen = expanded !== undefined && row.id === openId;
        const context = explain?.(row) ?? null;
        const detailsOpen = context !== null && detailed.has(row.id);
        return (
          <Fragment key={row.id}>
          <div
            data-testid="row"
            onClick={onOpen ? () => onOpen(row) : undefined}
            className={`flex flex-col gap-2 bg-page px-4 py-3.5 ${onOpen ? 'cursor-pointer active:bg-raised' : ''}`}
          >
            <span className="flex items-center gap-2.5 text-body font-semibold">
              {context && <DetailsButton open={detailsOpen} onToggle={() => onDetails(row.id)} controls={`details-${row.id}`} label={context.subject.title} />}
              {person && <Avatar name={person.name} size={26} agent={person.agent ? { label: person.agent } : undefined} />}
              {/* The title is the one real control of a card: a card that is itself a button cannot hold the details button beside it. */}
              {onOpen ? (
                <button
                  type="button"
                  aria-expanded={expanded !== undefined ? isOpen : undefined}
                  className="min-h-tap min-w-0 flex-1 text-left font-semibold [overflow-wrap:anywhere]"
                >
                  {title === null ? '—' : String(title)}
                </button>
              ) : (
                title === null ? '—' : String(title)
              )}
            </span>
            <dl className="m-0 grid grid-cols-[minmax(0,110px)_1fr] gap-x-3 gap-y-1">
              {rest.map((c) => {
                const chip = c.chip?.(row);
                const value = c.get(row);
                return (
                  <div key={c.label} className="contents">
                    <dt className="text-micro uppercase tracking-label text-meta">{c.label}</dt>
                    <dd className="m-0 min-w-0 text-dense text-secondary [overflow-wrap:anywhere]">
                      {chip ? <Badge tone={chip.tone}>{chip.label}</Badge> : value === null ? '—' : String(value)}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
          {detailsOpen && (
            <div className="bg-page p-4">
              <ContextPanel context={context} id={`details-${row.id}`} />
            </div>
          )}
          {isOpen && <div className="bg-page p-4">{expanded(row)}</div>}
          </Fragment>
        );
      })}
      {more && (
        <div className="bg-page p-3">
          <Button variant="quiet" onClick={more}>
            Show more · {shown} of {total}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * The gallery's RelatedList (G:356-377): a kind and its count, then rows that
 * each end in Open — and, when there are none, a line that says why. Every
 * row says what it is (ADR-0155): its `context` opens under it, so a list
 * here cannot leave a record unexplained any more than a `Table` can.
 */
export function RelatedList({
  title,
  rows,
  empty,
}: {
  title: string;
  rows: Array<{ id: string; title: string; sub?: string | undefined; onOpen?: (() => void) | undefined; context: ItemContext }>;
  empty: string;
}) {
  return (
    <section className="flex flex-col gap-2" aria-label={title}>
      <Kicker>
        {title} · {rows.length}
      </Kicker>
      {rows.length === 0 ? (
        <p className="m-0 text-dense text-meta">{empty}</p>
      ) : (
        <div className="flex flex-col gap-px border border-line bg-line">
          {rows.map((r) => (
            <div key={r.id} className="flex flex-col gap-1 bg-page px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-dense font-semibold">{r.title}</span>
                  {r.sub && <span className="truncate text-dense text-meta">{r.sub}</span>}
                </span>
                {r.onOpen && (
                  <Button icon="open" variant="quiet" size="row" onClick={r.onOpen}>Open</Button>
                )}
              </div>
              <Explainer context={r.context} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export type Fact = [label: string, value: ReactNode | null];

export function FactList({ facts }: { facts: Fact[] }) {
  const shown = facts.filter(([, v]) => v !== null && v !== undefined && v !== '');
  return (
    <dl className="m-0 grid grid-cols-[minmax(0,154px)_1fr] gap-px overflow-hidden border border-line bg-line phone:grid-cols-1">
      {shown.map(([label, value]) => (
        <div key={label} className="contents phone:flex phone:flex-col phone:bg-page">
          <dt className="flex min-h-[46px] items-center bg-page px-4 py-2 text-dense text-muted phone:min-h-0 phone:pb-0 phone:pt-3 phone:text-label phone:uppercase phone:tracking-label phone:text-meta">{label}</dt>
          <dd className="m-0 flex min-h-[46px] items-center bg-page px-4 py-2 text-dense [overflow-wrap:anywhere] phone:min-h-0 phone:pb-3 phone:pt-1">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export interface TimelineEntry {
  id: string;
  title: ReactNode;
  body?: ReactNode;
  when: string;
  current?: boolean | undefined;
  chip?: { label: string; tone: Tone } | undefined;
  /** What the step's record is, who brought it and what next (ADR-0155), opened under it. */
  context?: ItemContext | undefined;
}

export function Timeline({ title, entries, note }: { title: string; entries: TimelineEntry[]; note?: string | undefined }) {
  return (
    <section className="flex flex-col gap-3" aria-label={title}>
      <Kicker>{title}</Kicker>
      {entries.length === 0 && <p className="m-0 text-dense text-secondary">Nothing yet.</p>}
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        {entries.map((e) => (
          <li key={e.id} className="relative grid grid-cols-[9px_1fr] gap-3">
            {/* The 1px thread joining one step to the next (gallery G:384). */}
            <span aria-hidden className="absolute bottom-[-12px] left-[4px] top-[22px] w-px bg-line last:hidden" />
            <span className={`relative mt-2 h-[9px] w-[9px] ${e.current ? 'bg-accent' : 'border border-control bg-page'}`} aria-hidden />
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2 text-body font-semibold">
                {e.title}
                {e.chip && <Badge tone={e.chip.tone}>{e.chip.label}</Badge>}
              </div>
              {e.body && <div className="text-dense text-secondary">{e.body}</div>}
              <span className="text-dense text-meta">{e.when}</span>
              {e.context && <Explainer context={e.context} />}
            </div>
          </li>
        ))}
      </ol>
      {note && <p className="m-0 border-l-2 border-accent pl-3 text-dense text-secondary"><Plain>{note}</Plain></p>}
    </section>
  );
}

/**
 * A record's head (P:549-578): its mark, what it is, its name, the lines
 * that say what it is to us, and what can be done — asking about it first.
 * The header over the page names it too, so the name here is the page's
 * second heading, not its first.
 */
export function RecordHeader({
  kicker,
  title,
  subtitle,
  meta,
  chips,
  actions,
  mark,
  ask = true,
  back,
}: {
  kicker: string;
  title: string;
  subtitle?: string | null | undefined;
  meta?: string | null | undefined;
  chips?: Array<{ label: string; tone: Tone }> | undefined;
  actions?: ReactNode;
  /** A person or an agent shows as initials; a thing as the grey square. */
  mark?: { person: string; agent?: string | undefined; suspended?: boolean | undefined } | undefined;
  ask?: boolean | undefined;
  back?: { to: string; label: string } | undefined;
}) {
  const frame = useFrameMaybe();
  return (
    <header className="flex flex-col gap-5">
      {!frame?.inlineHead && <PageHeader kicker={kicker} title={title} back={back} />}
      <div className="flex items-start gap-4">
        {mark ? (
          <Avatar
            name={mark.person}
            size={48}
            agent={mark.agent ? { label: mark.agent } : undefined}
            state={mark.suspended ? 'suspended' : undefined}
          />
        ) : (
          <span aria-hidden className="flex h-12 w-12 shrink-0 items-center justify-center border border-line bg-sunken">
            <span className="block h-3.5 w-3.5 bg-line" />
          </span>
        )}
        <div className="flex min-w-0 flex-col gap-1">
          <Kicker>{kicker}</Kicker>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="m-0 text-screen font-semibold leading-tight">{title}</h2>
            {chips?.map((c) => (
              <Badge key={c.label} tone={c.tone}>
                {c.label}
              </Badge>
            ))}
          </div>
          {subtitle && <p className="m-0 text-dense text-secondary"><Plain>{subtitle}</Plain></p>}
          {meta && <p className="m-0 text-dense text-meta"><Plain>{meta}</Plain></p>}
        </div>
      </div>
      {(actions || (ask && frame)) && (
        <div className="flex flex-wrap gap-2">
          {ask && frame && (
            <Button icon="ask" variant="primary" onClick={() => frame.ask()}>Ask about this</Button>
          )}
          {actions}
        </div>
      )}
    </header>
  );
}

/**
 * The §17 token panel. A critical-tier flow's last step: the operation, the
 * exact target the token is bound to, and the token itself.
 *
 * A superadmin mints it here, from their own session, through the admin
 * door — one call, one operation, one target, and the plaintext is shown
 * to nobody: it goes straight into the flow that spends it. Anyone else
 * pastes a token a superadmin minted for exactly this, with
 * `pnpm supervision:mint`. Either way it is spent the moment the flow goes
 * ahead, and a retry needs a new one.
 */
export function TokenPanel({
  operation,
  target,
  token,
  onToken,
  canMint = false,
}: {
  operation: string;
  target: string;
  token: string;
  onToken: (token: string) => void;
  /** Whether the person looking is a superadmin, who may mint it here. */
  canMint?: boolean | undefined;
}) {
  const mint = useMintToken();
  const minted = mint.isSuccess && mint.data.token === token && token !== '';
  // The operation, its bound target and the command that mints it are the
  // superadmin's words; anyone else is told who approves this step.
  const tech = useTechnical();
  if (!tech && !canMint) {
    return (
      <div className="flex flex-col gap-3 border border-danger p-4" data-testid="token-panel">
        <div className="flex items-center gap-2 text-label uppercase tracking-label text-danger">
          <span className="inline-block h-[9px] w-[9px] bg-danger" aria-hidden />
          A superadmin approves this step
        </div>
        <p className="m-0 text-dense text-secondary">
          Ask a superadmin to approve it. If they give you a one-time code for it, paste it here; it works once, for this alone.
        </p>
        <label className="flex flex-col gap-1">
          <span className="text-label uppercase tracking-label text-meta">The code</span>
          <input
            className="min-h-tap border border-control bg-raised px-3 font-mono text-dense"
            value={token}
            onChange={(e) => onToken(e.target.value)}
            placeholder="Paste the code a superadmin gave you"
            aria-label="Supervision token"
            autoComplete="off"
          />
        </label>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3 border border-danger p-4" data-testid="token-panel">
      <div className="flex items-center gap-2 text-label uppercase tracking-label text-danger">
        <span className="inline-block h-[9px] w-[9px] bg-danger" aria-hidden />
        Single-use · bound to one target
      </div>
      <dl className="m-0 grid grid-cols-[110px_1fr] gap-y-2 text-dense">
        <dt className="text-meta">Approves</dt>
        <dd className="m-0">
          {operationWords(operation)} <span className="font-mono text-label text-meta">{operation}</span>
        </dd>
        <dt className="text-meta">For</dt>
        <dd className="m-0">
          {targetWords({ target })} <span className="break-all font-mono text-label text-meta">{target}</span>
        </dd>
      </dl>
      {canMint ? (
        <div className="flex flex-col gap-2">
          {minted ? (
            <p className="m-0 text-dense text-secondary" data-testid="token-minted">
              Minted. It is spent the moment you go ahead, and a retry needs a new one.
            </p>
          ) : (
            <Button
              variant="secondary"
              onClick={() =>
                mint.mutate(
                  { operation, target },
                  { onSuccess: (m) => onToken(m.token) },
                )
              }
              disabled={mint.isPending}
            >
              {mint.isPending ? 'Minting…' : 'Mint the token'}
            </Button>
          )}
          {mint.isError && (
            <p className="m-0 text-dense text-danger">{remedy(failureOf(mint.error)) || 'The token could not be minted.'}</p>
          )}
        </div>
      ) : (
        <>
          <label className="flex flex-col gap-1">
            <span className="text-label uppercase tracking-label text-meta">The token</span>
            <input
              className="min-h-tap border border-control bg-raised px-3 font-mono text-dense"
              value={token}
              onChange={(e) => onToken(e.target.value)}
              placeholder="Paste the token a superadmin minted for exactly this"
              aria-label="Supervision token"
              autoComplete="off"
            />
          </label>
          <p className="m-0 text-dense text-secondary">
            It is spent the moment you go ahead, and a retry needs a new one. A superadmin mints it with
            <code className="ml-1 font-mono text-dense">pnpm supervision:mint --operation {operation} --target {target}</code>
          </p>
        </>
      )}
    </div>
  );
}

/** A choice with a hint, the gallery's OptionRow. */
export function OptionRow<V extends string>({
  name,
  value,
  chosen,
  onChoose,
  options,
}: {
  name: string;
  value: V | null;
  chosen?: undefined;
  onChoose: (v: V) => void;
  options: Array<{ value: V; label: string; hint?: string }>;
}) {
  void chosen;
  return (
    <div role="radiogroup" aria-label={name} className="flex max-h-[260px] flex-col gap-1 overflow-y-auto">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChoose(o.value)}
            className={`flex min-h-tap items-start gap-3 border px-3 py-2.5 text-left ${
              on ? 'border-ink bg-raised' : 'border-line hover:border-control'
            }`}
          >
            <span className={`mt-1 inline-block h-[13px] w-[13px] shrink-0 border ${on ? 'border-ink bg-ink' : 'border-control'}`} aria-hidden />
            <span className="flex flex-col">
              <span className="text-body font-semibold">{o.label}</span>
              {o.hint && <span className="text-dense text-secondary">{o.hint}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export const when = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export const money = (amount: unknown, currency: unknown): string => {
  const n = typeof amount === 'string' ? Number(amount) : typeof amount === 'number' ? amount : NaN;
  if (Number.isNaN(n)) return '—';
  const cur = typeof currency === 'string' ? currency.trim() : '';
  return `${n.toLocaleString('en-GB', { maximumFractionDigits: 2 })}${cur ? ` ${cur}` : ''}`;
};
